import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import notFoundPerson from '../logo/not-found-person.png';
const formatUserName = (userData) => {
  const rawName =
    userData?.[0]?.UserName ||
    userData?.UserName ||
    userData?.userName ||
    '';

  return rawName
    .toString()
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const getGreeting = (hour) => {
  if (hour >= 5 && hour < 12) return 'Good Morning';
  if (hour >= 12 && hour < 17) return 'Good Afternoon';
  return 'Good Evening';
};

const parsePrivileges = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;

  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
};

const PageNotFound = () => {
  const navigate = useNavigate();
  const [currentDateTime, setCurrentDateTime] = useState(new Date());

  const userData = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('userData'));
    } catch (error) {
      return null;
    }
  }, []);

  const userName = formatUserName(userData) || 'Team Member';
  const userInitial = userName.charAt(0).toUpperCase();
  const privileges = parsePrivileges(localStorage.getItem('Privilliages'));
  const hasPrivileges = privileges.length > 0;

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentDateTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const greeting = getGreeting(currentDateTime.getHours());
  const liveTime = currentDateTime.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
  const liveDate = currentDateTime.toLocaleDateString([], {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
  const monthLabel = currentDateTime.toLocaleDateString([], {
    month: 'long',
    year: 'numeric',
  });
  const weekDay = currentDateTime.toLocaleDateString([], {
    weekday: 'short',
  });
  const dayNumber = currentDateTime.getDate();
  const mockRows = [0, 1, 2, 3];
  const mockCalendarDays = Array.from({ length: 15 }, (_, index) => index);
  const accentDots = [
    { top: '5%', left: '43%', size: 10, fill: '#5370ee' },
    { top: '16%', right: '5%', size: 10, border: '#7ca4ff' },
    { top: '25%', right: '20%', size: 12, fill: '#5370ee' },
    { top: '58%', left: '52%', size: 12, border: '#2ec8eb' },
    { top: '70%', left: '12%', size: 18, border: '#2ec8eb' },
    { bottom: '19%', left: '25%', size: 38, border: 'rgba(92, 115, 243, 0.45)' },
    { bottom: '16%', right: '2%', size: 108, border: 'rgba(113, 136, 245, 0.24)' },
    { bottom: '12%', right: '34%', size: 24, border: 'rgba(113, 136, 245, 0.32)' },
  ];

  return (
    <Box
      sx={{
        minHeight: 'calc(100vh - 96px)',
        px: { xs: 2.5, sm: 3, md: 2, lg: 5 },
        py: { xs: 4, sm: 4.5, md: 0.2 },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zoom: 0.78,
        // background: `
        //   radial-gradient(circle at top left, rgba(86, 208, 255, 0.18), transparent 28%),
        //   radial-gradient(circle at bottom right, rgba(76, 110, 245, 0.16), transparent 30%),
        //   linear-gradient(180deg, #f2f7ff 0%, #eef4ff 42%, #fbfdff 100%)
        // `,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: '1380px',
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            lg: 'minmax(400px, 0.95fr) minmax(460px, 1.05fr)',
          },
          gap: { xs: 5, md: 6, lg: 5 },
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
            maxWidth: { lg: '560px' },
            mx: { xs: 'auto', lg: 0 },
          }}
        >
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              px: 2,
              py: 0.9,
              borderRadius: '999px',
              background: 'rgba(255,255,255,0.72)',
              border: '1px solid rgba(126, 156, 255, 0.22)',
              boxShadow: '0 14px 30px rgba(116, 146, 226, 0.12)',
              color: '#5370cc',
              fontSize: '0.82rem',
              fontWeight: 700,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
            }}
          >
            Smart Redirect
          </Box>

          <Typography
            sx={{
              mt: 2.5,
              fontSize: 'clamp(3rem, 7vw, 5.5rem)',
              fontWeight: 800,
              lineHeight: { xs: 0.96, md: 0.92 },
              fontFamily: "'Poppins', sans-serif",
              letterSpacing: '0.06em',
              color: '#4f6df3',
              textShadow: '0 14px 30px rgba(79, 109, 243, 0.12)',
            }}
          >
            Welcome
          </Typography>

          <Typography
            sx={{
              mt: 1.5,
              fontSize: 'clamp(1.15rem, 2.3vw, 1.8rem)',
              fontWeight: 700,
              color: '#1f2e57',
            }}
          >
            {greeting}, {userName}
          </Typography>

          <Typography
            sx={{
              mt: 1,
              fontSize: 'clamp(1rem, 1.5vw, 1.1rem)',
              color: '#61739f',
              maxWidth: '540px',
              lineHeight: 1.75,
            }}
          >
            The page you are looking for is unavailable right now, but we can help
            you get back on track quickly.
          </Typography>

          <Box
            sx={{
              mt: { xs: 4.5, md: 5.5 },
              p: { xs: 3, md: 4, lg: 4.5 },
              borderRadius: 'clamp(24px, 3vw, 34px)',
              background: 'rgba(255,255,255,0.78)',
              border: '1px solid rgba(145, 171, 255, 0.26)',
              boxShadow: '0 28px 60px rgba(114, 140, 212, 0.16)',
              backdropFilter: 'blur(16px)',
            }}
          >
            <Typography
              sx={{
                fontSize: 'clamp(2rem, 5vw, 3.3rem)',
                lineHeight: 0.98,
                fontWeight: 800,
                color: '#1f2e57',
                letterSpacing: '-0.04em',
              }}
            >
              Let&apos;s give it a try
            </Typography>

            <Typography
              sx={{
                mt: 2.5,
                color: '#7080aa',
                fontSize: { xs: '1rem', md: '1.18rem' },
                lineHeight: 1.75,
              }}
            >
              Use the quick
              actions below to return home or revisit your previous screen.
            </Typography>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2.5}
              sx={{ mt: 4.5 }}
            >
              <Button
                onClick={() => navigate('/')}
                variant="contained"
                sx={{
                  minWidth: 190,
                  px: 4,
                  py: 1.8,
                  borderRadius: '999px',
                  fontSize: '1rem',
                  fontWeight: 700,
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  background: 'linear-gradient(135deg, #4c6ef5 0%, #2ec5f3 100%)',
                  boxShadow: '0 18px 35px rgba(76, 110, 245, 0.28)',
                }}
              >
                Dashboard
              </Button>
              <Button
                onClick={() => navigate(-1)}
                variant="outlined"
                sx={{
                  minWidth: 190,
                  px: 4,
                  py: 1.8,
                  borderRadius: '999px',
                  fontSize: '1rem',
                  fontWeight: 700,
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  color: '#4563da',
                  borderColor: 'rgba(87, 113, 215, 0.24)',
                  background: 'rgba(255,255,255,0.72)',
                  '&:hover': {
                    borderColor: '#4c6ef5',
                    background: 'rgba(255,255,255,0.95)',
                  },
                }}
              >
                Go Back
              </Button>
            </Stack>

            {!hasPrivileges && (
              <Typography
                sx={{
                  mt: 2.5,
                  color: '#8a93b6',
                  fontSize: '0.95rem',
                }}
              >
                If you expected access here, your role permissions may need an update.
              </Typography>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            position: 'relative',
            width: '100%',
            maxWidth: { xs: 420, sm: 480, md: 560, lg: 620 },
            aspectRatio: { xs: '1 / 1.18', md: '1 / 1.12' },
            minHeight: { xs: 420, md: 520 },
            mx: 'auto',
          }}
        >
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              height: '100%',
              transform: {
                xs: 'scale(0.9)',
                sm: 'scale(0.96)',
                md: 'scale(1)',
              },
              transformOrigin: 'center center',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                inset: { xs: '8% 5%', md: '6% 5%' },
                borderRadius: 'clamp(32px, 5vw, 54px)',
                background:
                  'radial-gradient(circle at 30% 20%, rgba(255,255,255,0.96) 0%, rgba(239,245,255,0.92) 46%, rgba(222,232,255,0.88) 100%)',
                border: '1px solid rgba(170, 191, 255, 0.24)',
              }}
            />

            <Box
              sx={{
                position: 'absolute',
                bottom: '1%',
                right: '2%',
                width: '58%',
                aspectRatio: '1 / 1',
                borderRadius: '50%',
                background: 'rgba(133, 171, 255, 0.18)',
              }}
            />

            <Box
              sx={{
                position: 'absolute',
                bottom: '23%',
                left: '7%',
                width: '15%',
                height: '30%',
                borderTopLeftRadius: '90px',
                borderTopRightRadius: '90px',
                borderBottomLeftRadius: '18px',
                borderBottomRightRadius: '18px',
                background: 'rgba(113, 135, 241, 0.22)',
                transform: 'rotate(-28deg)',
              }}
            />

            <Box
              sx={{
                position: 'absolute',
                bottom: '8%',
                left: '25%',
                width: '15%',
                height: '32%',
                borderTopLeftRadius: '90px',
                borderTopRightRadius: '90px',
                borderBottomLeftRadius: '18px',
                borderBottomRightRadius: '18px',
                background: 'rgba(96, 190, 235, 0.18)',
                transform: 'rotate(16deg)',
              }}
            />

            <Box
              sx={{
                position: 'absolute',
                top: '4%',
                right: '7%',
                width: '48%',
                p: { xs: 2.1, md: 2.5 },
                borderRadius: '28px',
                background: 'linear-gradient(135deg, #4d67f4 0%, #3a57e8 100%)',
                boxShadow: '0 22px 42px rgba(66, 91, 228, 0.26)',
                color: '#fff',
                zIndex: 3,
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Box
                  sx={{
                    width: 58,
                    height: 58,
                    borderRadius: '50%',
                    background: 'linear-gradient(180deg, #2fd2eb 0%, #7ed7ff 100%)',
                    p: '4px',
                    flexShrink: 0,
                  }}
                >
                  <Box
                    sx={{
                      width: '100%',
                      height: '100%',
                      borderRadius: '50%',
                      background: 'linear-gradient(180deg, #ffcf8e 0%, #ff7f73 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 800,
                      fontSize: '1.2rem',
                    }}
                  >
                    {userInitial}
                  </Box>
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: { xs: '1.55rem', md: '2.05rem' },
                      fontWeight: 800,
                      lineHeight: 1,
                      letterSpacing: '-0.04em',
                    }}
                  >
                    {liveTime.replace(/\s/g, '')}
                  </Typography>
                  <Typography
                    sx={{
                      mt: 0.7,
                      fontSize: '0.95rem',
                      opacity: 0.96,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {userName}
                  </Typography>
                  <Typography sx={{ fontSize: '0.82rem', opacity: 0.76 }}>
                    {liveDate}
                  </Typography>
                </Box>
              </Stack>
            </Box>

            <Box
              sx={{
                position: 'absolute',
                top: '18%',
                left: '10%',
                width: '54%',
                p: { xs: 2.3, md: 3 },
                borderRadius: '30px',
                background: 'rgba(255,255,255,0.88)',
                border: '1px solid rgba(235, 239, 255, 0.9)',
                boxShadow: '0 24px 40px rgba(30, 40, 70, 0.15)',
                zIndex: 2,
              }}
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: 1.6,
                  alignItems: 'center',
                }}
              >
                {mockRows.map((index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.2,
                      opacity: index === 1 ? 1 : 0.48,
                    }}
                  >
                    <Box
                      sx={{
                        width: 42,
                        height: 42,
                        borderRadius: '50%',
                        background: index === 1 ? '#37caeb' : '#dbe0df',
                      }}
                    />
                    <Box
                      sx={{
                        height: 8,
                        width: 34,
                        borderRadius: '999px',
                        background: index === 1 ? '#37caeb' : '#dbe0df',
                      }}
                    />
                  </Box>
                ))}
              </Box>
            </Box>

            <Box
              sx={{
                position: 'absolute',
                top: '48%',
                left: '6%',
                width: '42%',
                p: 2.2,
                borderRadius: '28px',
                background: 'rgba(255,255,255,0.94)',
                boxShadow: '0 20px 36px rgba(127, 145, 204, 0.15)',
                zIndex: 2,
              }}
            >
              <Box
                sx={{
                  height: 34,
                  mx: -2.2,
                  mt: -2.2,
                  mb: 2.2,
                  borderTopLeftRadius: '28px',
                  borderTopRightRadius: '28px',
                  background: 'linear-gradient(90deg, #4d67f4 0%, #7092ff 100%)',
                }}
              />

              <Typography sx={{ mb: 1.5, fontWeight: 700, color: '#657298' }}>
                {monthLabel}
              </Typography>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(5, 1fr)',
                  gap: 1,
                }}
              >
                {mockCalendarDays.map((index) => {
                  const isActive = index === 13;
                  return (
                    <Box
                      key={index}
                      sx={{
                        aspectRatio: '1 / 1',
                        borderRadius: '8px',
                        background: isActive
                          ? 'linear-gradient(135deg, #3dd2ef 0%, #22b8ea 100%)'
                          : 'linear-gradient(135deg, #bfd5ff 0%, #a8c4ff 100%)',
                      }}
                    />
                  );
                })}
              </Box>
            </Box>

            <Box
              sx={{
                position: 'absolute',
                right: '18%',
                bottom: '13%',
                width: '36%',
                display: 'flex',
                alignItems: 'center',
                borderRadius: '22px',
                overflow: 'hidden',
                boxShadow: '0 20px 36px rgba(44, 198, 235, 0.2)',
                background: 'linear-gradient(135deg, #37d4ef 0%, #27c2ee 100%)',
                zIndex: 3,
              }}
            >
              <Box
                sx={{
                  px: 3,
                  py: 1.7,
                  background: 'linear-gradient(135deg, #37d4ef 0%, #27c2ee 100%)',
                  color: '#fff',
                  fontSize: { xs: '1.2rem', md: '1.55rem' },
                  fontWeight: 800,
                  letterSpacing: '0.06em',
                }}
              >
                {weekDay.toUpperCase()}
              </Box>
              <Box
                sx={{
                  px: 3,
                  py: 1.7,
                  background: 'linear-gradient(135deg, #37d4ef 0%, #27c2ee 100%)',
                  color: '#fff',
                  fontSize: { xs: '1.2rem', md: '1.55rem' },
                  fontWeight: 800,
                }}
              >
                {dayNumber}
              </Box>
            </Box>

            <Box
              component="img"
              src={notFoundPerson}
              alt="Not found illustration"
              sx={{
                position: 'absolute',
                top: '22%',
                right: '4%',
                zIndex: 2,
                width: '48%',
                maxHeight: '62%',
                objectFit: 'contain',
                filter: 'drop-shadow(0 24px 40px rgba(100, 126, 214, 0.18))',
              }}
            />

            {accentDots.map(({ size, fill, border, ...position }, index) => (
              <Box
                key={index}
                sx={{
                  position: 'absolute',
                  ...position,
                  width: size,
                  height: size,
                  borderRadius: '50%',
                  background: fill || 'transparent',
                  border: border ? `3px solid ${border}` : 'none',
                  zIndex: 1,
                }}
              />
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default PageNotFound;
